import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { createChart, CandlestickSeries, BarSeries, LineSeries, AreaSeries, BaselineSeries, HistogramSeries, createSeriesMarkers } from 'lightweight-charts';
import { IchimokuCloudPrimitive } from '../utils/ichimokuCloudPrimitive';
import { SessionHighlightPrimitive } from '../utils/sessionHighlightPrimitive';
import { NewsMarkerPrimitive } from '../utils/newsMarkerPrimitive';
import { VolumeProfilePrimitive } from '../utils/volumeProfilePrimitive';
import { MarketProfilePrimitive } from '../utils/marketProfilePrimitive';

function getChartColors() {
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  return {
    background: isDark ? '#0f1117' : '#ffffff',
    text: isDark ? '#8b8fa3' : '#6b7080',
    grid: isDark ? '#1e2030' : '#e2e4e9',
    upColor: '#22c55e',
    downColor: '#ef4444',
    borderUpColor: '#22c55e',
    borderDownColor: '#ef4444',
    wickUpColor: '#22c55e',
    wickDownColor: '#ef4444',
    volumeUp: 'rgba(34, 197, 94, 0.3)',
    volumeDown: 'rgba(239, 68, 68, 0.3)',
  };
}
const CHART_COLORS = getChartColors();

// ── Chart type constants ──
export const CHART_TYPES = [
  { id: 'candles', label: 'Candles', icon: '🕯' },
  { id: 'bars', label: 'Bars', icon: '┃' },
  { id: 'heikinAshi', label: 'Heikin Ashi', icon: '⬢' },
  { id: 'hollow', label: 'Hollow', icon: '▯' },
  { id: 'line', label: 'Line', icon: '─' },
  { id: 'area', label: 'Area', icon: '▨' },
  { id: 'baseline', label: 'Baseline', icon: '⏤' },
  { id: 'renko', label: 'Renko', icon: '▦' },
];

function toHeikinAshi(data) {
  const result = [];
  for (let i = 0; i < data.length; i++) {
    const d = data[i];
    const prev = result[i - 1];
    const haClose = (d.open + d.high + d.low + d.close) / 4;
    const haOpen = prev ? (prev.open + prev.close) / 2 : (d.open + d.close) / 2;
    const haHigh = Math.max(d.high, haOpen, haClose);
    const haLow = Math.min(d.low, haOpen, haClose);
    result.push({ time: d.time, open: haOpen, high: haHigh, low: haLow, close: haClose });
  }
  return result;
}

function toRenko(data, brickPct = 0.002) {
  if (data.length < 2) return data;
  // Auto brick size: percentage of average price
  const avgPrice = data.reduce((s, d) => s + d.close, 0) / data.length;
  const brickSize = avgPrice * brickPct;
  const result = [];
  let lastClose = Math.round(data[0].close / brickSize) * brickSize;

  for (const d of data) {
    while (d.close >= lastClose + brickSize) {
      const open = lastClose;
      lastClose += brickSize;
      result.push({ time: d.time, open, high: lastClose, low: open, close: lastClose });
    }
    while (d.close <= lastClose - brickSize) {
      const open = lastClose;
      lastClose -= brickSize;
      result.push({ time: d.time, open, high: open, low: lastClose, close: lastClose });
    }
  }
  return result.length > 0 ? result : data;
}

const CandlestickChart = forwardRef(function CandlestickChart({
  candles,
  height = 500,
  showVolume = true,
  chartType = 'candles',
  overlayIndicators = [],
  panelIndicators = [],
  tradeMarkers = [],
  priceLines = [],
  onChartClick,
  onChartMouseMove,
  onChartDblClick,
  onPriceLineDrag,
  // Panel controls
  onRemoveIndicator,
  onOpenIndicatorSettings,
  // Economic news
  newsEvents = [],
  // Scale
  logScale = false,
  // Crosshair sync
  onCrosshairSync,
  syncCrosshairTime,
  // Volume profile / Market profile
  showVolumeProfile = false,
  showMarketProfile = false,
  tzOffset = 0,
  watermark = '',
  watermarkSize = 5,
  watermarkOpacity = 8,
  watermarkColor = '#c8d2f0',
}, ref) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const chartRef = useRef(null);

  const candleSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const overlaySeriesRef = useRef(new Map());
  // Panel tracking: instanceId -> { paneIndex, series: Map<key, series>, levelLines: [] }
  const panelStateRef = useRef(new Map());
  const priceLinesRef = useRef([]);
  const ichimokuCloudRef = useRef(null);
  const ichimokuAttachedRef = useRef(false);
  const sessionPrimitiveRef = useRef(null);
  const sessionAttachedRef = useRef(false);
  const newsPrimitiveRef = useRef(null);
  const newsAttachedRef = useRef(false);
  const markersRef = useRef(null);
  const onChartClickRef = useRef(onChartClick);
  onChartClickRef.current = onChartClick;
  const onChartMouseMoveRef = useRef(onChartMouseMove);
  onChartMouseMoveRef.current = onChartMouseMove;
  const onChartDblClickRef = useRef(onChartDblClick);
  onChartDblClickRef.current = onChartDblClick;
  const onCrosshairSyncRef = useRef(onCrosshairSync);
  onCrosshairSyncRef.current = onCrosshairSync;

  // Crosshair values for panel headers
  const [panelCrosshairValues, setPanelCrosshairValues] = useState({});
  const [newsTooltip, setNewsTooltip] = useState(null); // {x, events} or null
  const newsEventsRef = useRef(newsEvents);
  newsEventsRef.current = newsEvents;

  useImperativeHandle(ref, () => ({
    get current() { return wrapperRef.current; },
    querySelector: (s) => wrapperRef.current?.querySelector(s),
    getChart: () => chartRef.current,
    getCandleSeries: () => candleSeriesRef.current,
    getSeriesForPane: (paneIndex) => {
      if (paneIndex === 0 || paneIndex == null) return candleSeriesRef.current;
      for (const [, ps] of panelStateRef.current) {
        if (ps.paneIndex === paneIndex && ps.series.size > 0) {
          return ps.series.values().next().value;
        }
      }
      return candleSeriesRef.current;
    },
    setCrosshairPosition: (price, time) => {
      const chart = chartRef.current;
      const series = candleSeriesRef.current;
      if (chart && series) {
        try { chart.setCrosshairPosition(price, time, series); } catch {}
      }
    },
    clearCrosshairPosition: () => {
      try { chartRef.current?.clearCrosshairPosition(); } catch {}
    },
  }), []);

  // ── Create chart once ──
  useEffect(() => {
    if (!containerRef.current) return;

    const colors = getChartColors();
    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height,
      layout: {
        background: { color: colors.background },
        textColor: colors.text,
        attributionLogo: false,
        panes: {
          separatorColor: colors.grid,
          separatorHoverColor: 'rgba(99, 102, 241, 0.4)',
          enableResize: true,
        },
      },
      grid: {
        vertLines: { color: colors.grid },
        horzLines: { color: colors.grid },
      },
      crosshair: { mode: 0 },
      rightPriceScale: { borderColor: colors.grid },
      timeScale: {
        borderColor: colors.grid,
        timeVisible: true,
        secondsVisible: false,
      },
    });

    // Pane 0: main price series (type depends on chartType)
    let candleSeries;
    if (chartType === 'bars') {
      candleSeries = chart.addSeries(BarSeries, {
        upColor: CHART_COLORS.upColor,
        downColor: CHART_COLORS.downColor,
        openVisible: true,
        thinBars: true,
      }, 0);
    } else if (chartType === 'line') {
      candleSeries = chart.addSeries(LineSeries, {
        color: '#3b82f6',
        lineWidth: 2,
      }, 0);
    } else if (chartType === 'area') {
      candleSeries = chart.addSeries(AreaSeries, {
        lineColor: '#3b82f6',
        topColor: 'rgba(59, 130, 246, 0.3)',
        bottomColor: 'rgba(59, 130, 246, 0.02)',
        lineWidth: 2,
      }, 0);
    } else if (chartType === 'baseline') {
      candleSeries = chart.addSeries(BaselineSeries, {
        baseValue: { type: 'price', price: 0 }, // updated in data effect
        topLineColor: CHART_COLORS.upColor,
        topFillColor1: 'rgba(34, 197, 94, 0.2)',
        topFillColor2: 'rgba(34, 197, 94, 0.02)',
        bottomLineColor: CHART_COLORS.downColor,
        bottomFillColor1: 'rgba(239, 68, 68, 0.02)',
        bottomFillColor2: 'rgba(239, 68, 68, 0.2)',
        lineWidth: 2,
      }, 0);
    } else if (chartType === 'hollow') {
      candleSeries = chart.addSeries(CandlestickSeries, {
        upColor: 'transparent',
        downColor: CHART_COLORS.downColor,
        borderVisible: true,
        borderUpColor: CHART_COLORS.upColor,
        borderDownColor: CHART_COLORS.downColor,
        wickUpColor: CHART_COLORS.upColor,
        wickDownColor: CHART_COLORS.downColor,
      }, 0);
    } else {
      // 'candles' or 'heikinAshi' — both use CandlestickSeries
      candleSeries = chart.addSeries(CandlestickSeries, {
        upColor: CHART_COLORS.upColor,
        downColor: CHART_COLORS.downColor,
        borderUpColor: CHART_COLORS.borderUpColor,
        borderDownColor: CHART_COLORS.borderDownColor,
        wickUpColor: CHART_COLORS.wickUpColor,
        wickDownColor: CHART_COLORS.wickDownColor,
      }, 0);
    }

    // Always create volume series (toggle visibility dynamically)
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
      visible: showVolume,
    }, 0);
    chart.panes()[0].priceScale('volume').applyOptions({
      scaleMargins: { top: 0.85, bottom: 0 },
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;
    overlaySeriesRef.current = new Map();
    panelStateRef.current = new Map();

    // Helper: resolve correct series for a given pane
    const resolveSeriesForPane = (paneIndex) => {
      if (paneIndex === 0 || paneIndex == null) return candleSeries;
      for (const [, ps] of panelStateRef.current) {
        if (ps.paneIndex === paneIndex && ps.series.size > 0) {
          return ps.series.values().next().value;
        }
      }
      return candleSeries;
    };

    // Click handler
    chart.subscribeClick((param) => {
      if (onChartClickRef.current && param.point) {
        const paneIndex = param.paneIndex ?? 0;
        const series = resolveSeriesForPane(paneIndex);
        const logical = chart.timeScale().coordinateToLogical(param.point.x);
        const price = series.coordinateToPrice(param.point.y);
        onChartClickRef.current({ logical, price, point: param.point, time: param.time, paneIndex });
      }
    });

    // Double-click handler
    chart.subscribeDblClick((param) => {
      if (onChartDblClickRef.current && param.point) {
        const paneIndex = param.paneIndex ?? 0;
        const series = resolveSeriesForPane(paneIndex);
        const logical = chart.timeScale().coordinateToLogical(param.point.x);
        const price = series.coordinateToPrice(param.point.y);
        onChartDblClickRef.current({ logical, price, point: param.point, time: param.time, paneIndex });
      }
    });

    // Crosshair move: extract panel values
    chart.subscribeCrosshairMove((param) => {
      if (onChartMouseMoveRef.current && param.point) {
        const paneIndex = param.paneIndex ?? 0;
        const series = resolveSeriesForPane(paneIndex);
        const logical = chart.timeScale().coordinateToLogical(param.point.x);
        const price = series.coordinateToPrice(param.point.y);
        if (logical != null && price != null) {
          onChartMouseMoveRef.current({ logical, price, point: param.point, time: param.time, paneIndex });
        }
      }

      // Emit crosshair time for sync
      if (onCrosshairSyncRef.current) {
        onCrosshairSyncRef.current(param.time || null);
      }

      // Extract crosshair values for all panel series
      if (param.seriesData && panelStateRef.current.size > 0) {
        const vals = {};
        for (const [, ps] of panelStateRef.current) {
          for (const [key, series] of ps.series) {
            const data = param.seriesData.get(series);
            if (data) {
              vals[key] = data.value !== undefined ? data.value : data.close;
            }
          }
        }
        setPanelCrosshairValues(vals);
      } else if (!param.time) {
        setPanelCrosshairValues({});
      }

      // Check for news events near the crosshair time
      const news = newsEventsRef.current;
      if (news && news.length > 0 && param.time && param.point) {
        const t = typeof param.time === 'object' ? new Date(param.time.year, param.time.month - 1, param.time.day).getTime() / 1000 : param.time;
        // Find events within 12 hours of the crosshair
        const nearby = news.filter((e) => Math.abs(e.time - t) < 43200);
        if (nearby.length > 0) {
          setNewsTooltip({ x: param.point.x, events: nearby.slice(0, 8) });
        } else {
          setNewsTooltip(null);
        }
      } else {
        setNewsTooltip(null);
      }
    });

    // Observe the wrapper (parent) so the chart fills all available space
    const resizeTarget = wrapperRef.current || containerRef.current;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        chart.applyOptions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    resizeObserver.observe(resizeTarget);

    // React to theme changes
    const themeObserver = new MutationObserver(() => {
      const c = getChartColors();
      chart.applyOptions({
        layout: { background: { color: c.background }, textColor: c.text, panes: { separatorColor: c.grid } },
        grid: { vertLines: { color: c.grid }, horzLines: { color: c.grid } },
        rightPriceScale: { borderColor: c.grid },
        timeScale: { borderColor: c.grid },
      });
    });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    return () => {
      themeObserver.disconnect();
      resizeObserver.disconnect();
      if (markersRef.current) { markersRef.current.detach(); markersRef.current = null; }
      ichimokuCloudRef.current = null;
      sessionPrimitiveRef.current = null;
      sessionAttachedRef.current = false;
      newsPrimitiveRef.current = null;
      newsAttachedRef.current = false;
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
      overlaySeriesRef.current = new Map();
      panelStateRef.current = new Map();
    };
  }, [height, chartType]);

  // ── Sync crosshair from other charts ──
  useEffect(() => {
    const chart = chartRef.current;
    const series = candleSeriesRef.current;
    if (!chart || !series || !syncCrosshairTime) return;
    try {
      const data = series.dataByIndex(0);
      if (data) chart.setCrosshairPosition(undefined, syncCrosshairTime, series);
    } catch {}
  }, [syncCrosshairTime]);

  // ── Volume Profile ──
  const vpRef = useRef(null);
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series) return;
    if (showVolumeProfile) {
      if (!vpRef.current) {
        vpRef.current = new VolumeProfilePrimitive();
        series.attachPrimitive(vpRef.current);
      }
      vpRef.current.updateCandles(candles);
    } else if (vpRef.current) {
      try { series.detachPrimitive(vpRef.current); } catch {}
      vpRef.current = null;
    }
  }, [showVolumeProfile, candles]);

  // ── Market Profile ──
  const mpRef = useRef(null);
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series) return;
    if (showMarketProfile) {
      if (!mpRef.current) {
        mpRef.current = new MarketProfilePrimitive();
        series.attachPrimitive(mpRef.current);
      }
      mpRef.current.updateCandles(candles);
    } else if (mpRef.current) {
      try { series.detachPrimitive(mpRef.current); } catch {}
      mpRef.current = null;
    }
  }, [showMarketProfile, candles]);

  // ── Log scale toggle ──
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.priceScale('right').applyOptions({ mode: logScale ? 1 : 0 });
  }, [logScale]);

  // ── Toggle volume visibility ──
  useEffect(() => {
    if (volumeSeriesRef.current) {
      volumeSeriesRef.current.applyOptions({ visible: showVolume });
    }
  }, [showVolume]);

  // ── Update candle data ──
  useEffect(() => {
    if (!candleSeriesRef.current || !candles || candles.length === 0) return;

    const isValueOnly = chartType === 'line' || chartType === 'area' || chartType === 'baseline';

    if (isValueOnly) {
      const valueData = candles.map((c) => ({ time: c.time, value: c.close }));
      candleSeriesRef.current.setData(valueData);
      // Set baseline to first candle's close
      if (chartType === 'baseline') {
        candleSeriesRef.current.applyOptions({
          baseValue: { type: 'price', price: candles[0].close },
        });
      }
    } else if (chartType === 'heikinAshi') {
      candleSeriesRef.current.setData(toHeikinAshi(candles));
    } else if (chartType === 'renko') {
      candleSeriesRef.current.setData(toRenko(candles));
    } else {
      candleSeriesRef.current.setData(candles);
    }

    if (volumeSeriesRef.current) {
      const volumeData = candles.map((c) => ({
        time: c.time,
        value: c.volume,
        color: c.close >= c.open ? CHART_COLORS.volumeUp : CHART_COLORS.volumeDown,
      }));
      volumeSeriesRef.current.setData(volumeData);
    }
  }, [candles, chartType]);

  // ── Update overlay indicators (pane 0) ──
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const currentKeys = new Set(overlayIndicators.map((o) => o.seriesKey));
    const existing = overlaySeriesRef.current;

    for (const [key, series] of existing) {
      if (!currentKeys.has(key)) {
        chart.removeSeries(series);
        existing.delete(key);
      }
    }

    // Collect Ichimoku cloud data and session data if present
    let ichCloudUp = null;
    let ichCloudDown = null;
    let sessionCandles = null;
    let sessionShowLabels = true;

    for (const overlay of overlayIndicators) {
      const keyParts = overlay.seriesKey.split('_');
      const seriesName = keyParts[keyParts.length - 1];

      // Detect session indicator — handle via primitive, skip series creation
      if (seriesName === 'sessionData' || overlay.seriesKey.includes('_sessionData')) {
        // Use the chart's candles prop (raw OHLCV) for session rendering
        sessionCandles = candles;
        sessionShowLabels = true;
        continue;
      }

      if (seriesName === 'cloudUp' || seriesName === 'cloudDown') {
        if (seriesName === 'cloudUp') ichCloudUp = overlay.data;
        if (seriesName === 'cloudDown') ichCloudDown = overlay.data;
        // Still create a hidden line series so the data is on the chart's timeScale
        let series = existing.get(overlay.seriesKey);
        if (!series) {
          series = chart.addSeries(LineSeries, {
            color: 'transparent',
            lineWidth: 0,
            priceScaleId: 'right',
            lastValueVisible: false,
            priceLineVisible: false,
            visible: false,
          }, 0);
          existing.set(overlay.seriesKey, series);
        }
        if (overlay.data && overlay.data.length > 0) {
          series.setData(overlay.data);
        }
        continue;
      }

      let series = existing.get(overlay.seriesKey);
      if (!series) {
        series = chart.addSeries(LineSeries, {
          color: overlay.hidden ? 'transparent' : overlay.color,
          lineWidth: overlay.hidden ? 0 : (overlay.lineWidth || 2),
          priceScaleId: 'right',
          lastValueVisible: false,
          priceLineVisible: false,
          visible: !overlay.hidden,
        }, 0);
        existing.set(overlay.seriesKey, series);
      } else {
        series.applyOptions({
          color: overlay.hidden ? 'transparent' : overlay.color,
          lineWidth: overlay.hidden ? 0 : (overlay.lineWidth || 2),
          visible: !overlay.hidden,
        });
      }
      if (overlay.data && overlay.data.length > 0) {
        series.setData(overlay.data);
      }
    }

    // Manage Ichimoku cloud primitive
    if (ichCloudUp && ichCloudDown && ichCloudUp.length > 0 && ichCloudDown.length > 0) {
      if (!ichimokuCloudRef.current) {
        ichimokuCloudRef.current = new IchimokuCloudPrimitive();
        ichimokuAttachedRef.current = false;
      }
      // Attach once
      if (!ichimokuAttachedRef.current) {
        const attachTarget = candleSeriesRef.current;
        if (attachTarget) {
          try {
            attachTarget.attachPrimitive(ichimokuCloudRef.current);
            ichimokuAttachedRef.current = true;
          } catch {}
        }
      }
      ichimokuCloudRef.current.setData(ichCloudUp, ichCloudDown);
    } else if (ichimokuCloudRef.current && ichimokuAttachedRef.current) {
      // No cloud data — detach
      try { candleSeriesRef.current?.detachPrimitive(ichimokuCloudRef.current); } catch {}
      ichimokuCloudRef.current = null;
      ichimokuAttachedRef.current = false;
    }

    // Manage session highlight primitive
    if (sessionCandles && sessionCandles.length > 0) {
      if (!sessionPrimitiveRef.current) {
        sessionPrimitiveRef.current = new SessionHighlightPrimitive();
        sessionAttachedRef.current = false;
      }
      if (!sessionAttachedRef.current && candleSeriesRef.current) {
        try {
          candleSeriesRef.current.attachPrimitive(sessionPrimitiveRef.current);
          sessionAttachedRef.current = true;
        } catch {}
      }
      sessionPrimitiveRef.current.setData(sessionCandles, null, sessionShowLabels, tzOffset);
    } else if (sessionPrimitiveRef.current && sessionAttachedRef.current) {
      try { candleSeriesRef.current?.detachPrimitive(sessionPrimitiveRef.current); } catch {}
      sessionPrimitiveRef.current = null;
      sessionAttachedRef.current = false;
    }
  }, [overlayIndicators, candles]);

  // ── Update panel indicators (native panes) ──
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const activeIds = new Set(panelIndicators.map((p) => p.instanceId));
    const existing = panelStateRef.current;

    // Remove panels that are no longer active
    // Collect pane indices to remove (in reverse order to avoid index shifting)
    const toRemove = [];
    for (const [instId, ps] of existing) {
      if (!activeIds.has(instId)) {
        toRemove.push({ instId, paneIndex: ps.paneIndex });
      }
    }
    // Sort descending by paneIndex so removal doesn't shift indices
    toRemove.sort((a, b) => b.paneIndex - a.paneIndex);
    for (const { instId, paneIndex } of toRemove) {
      try { chart.removePane(paneIndex); } catch {}
      existing.delete(instId);
      // Adjust paneIndex for remaining panels above the removed one
      for (const [, ps] of existing) {
        if (ps.paneIndex > paneIndex) ps.paneIndex--;
      }
    }

    // Add or update each panel indicator
    panelIndicators.forEach((panel, i) => {
      const targetPaneIndex = i + 1; // pane 0 = main chart
      let ps = existing.get(panel.instanceId);

      if (!ps) {
        // Create new pane for this indicator
        // Ensure we have enough panes
        while (chart.panes().length <= targetPaneIndex) {
          chart.addPane();
        }

        ps = { paneIndex: targetPaneIndex, series: new Map(), levelLines: [] };
        existing.set(panel.instanceId, ps);

        // Set stretch factor: main pane = 3, indicator panes = 1
        try {
          chart.panes()[0].setStretchFactor(3);
          chart.panes()[targetPaneIndex].setStretchFactor(1);
        } catch {}
      }

      const pane = chart.panes()[ps.paneIndex];
      if (!pane) return;

      // Remove series that are no longer in this panel's data
      const currentKeys = new Set(panel.seriesData.map((sd) => sd.key));
      for (const [key, series] of ps.series) {
        if (!currentKeys.has(key)) {
          try { chart.removeSeries(series); } catch {}
          ps.series.delete(key);
        }
      }

      // Add/update series
      let firstSeries = null;
      for (const sd of panel.seriesData) {
        let series = ps.series.get(sd.key);
        const isHistogram = sd.type === 'histogram';

        if (!series) {
          if (isHistogram) {
            series = chart.addSeries(HistogramSeries, {
              lastValueVisible: false,
              priceLineVisible: false,
              visible: !sd.hidden,
            }, ps.paneIndex);
          } else {
            series = chart.addSeries(LineSeries, {
              color: sd.hidden ? 'transparent' : (sd.color || '#6366f1'),
              lineWidth: sd.hidden ? 0 : (sd.lineWidth || 1),
              lastValueVisible: false,
              priceLineVisible: false,
              visible: !sd.hidden,
            }, ps.paneIndex);
          }
          ps.series.set(sd.key, series);
        } else {
          if (isHistogram) {
            series.applyOptions({ visible: !sd.hidden });
          } else {
            series.applyOptions({
              color: sd.hidden ? 'transparent' : (sd.color || '#6366f1'),
              lineWidth: sd.hidden ? 0 : (sd.lineWidth || 1),
              visible: !sd.hidden,
            });
          }
        }

        if (sd.data && sd.data.length > 0) {
          series.setData(sd.data);
        }

        if (!firstSeries) firstSeries = series;
      }

      // Reference levels (horizontal lines)
      if (firstSeries && panel.levels && panel.levels.length > 0) {
        // Remove old level lines
        for (const pl of ps.levelLines) {
          try { firstSeries.removePriceLine(pl); } catch {}
        }
        ps.levelLines = [];

        for (const lvl of panel.levels) {
          try {
            const pl = firstSeries.createPriceLine({
              price: lvl,
              color: 'rgba(139, 143, 163, 0.3)',
              lineWidth: 1,
              lineStyle: 2,
              axisLabelVisible: false,
            });
            ps.levelLines.push(pl);
          } catch {}
        }
      }
    });
  }, [panelIndicators]);

  // ── Trade markers ──
  useEffect(() => {
    if (!candleSeriesRef.current) return;
    if (!markersRef.current) {
      markersRef.current = createSeriesMarkers(candleSeriesRef.current);
    }
    const sorted = tradeMarkers.length > 0
      ? [...tradeMarkers].sort((a, b) => a.time - b.time)
      : [];
    markersRef.current.setMarkers(sorted);
  }, [tradeMarkers]);

  // ── Price lines (SL/TP) ──
  useEffect(() => {
    if (!candleSeriesRef.current) return;
    for (const pl of priceLinesRef.current) {
      candleSeriesRef.current.removePriceLine(pl);
    }
    priceLinesRef.current = [];
    for (const line of priceLines) {
      const pl = candleSeriesRef.current.createPriceLine({
        price: line.price,
        color: line.color,
        lineWidth: 1,
        lineStyle: line.lineStyle ?? 2,
        axisLabelVisible: true,
        title: line.title || '',
      });
      priceLinesRef.current.push(pl);
    }
  }, [priceLines]);

  // ── Draggable SL/TP price lines ──
  useEffect(() => {
    if (!containerRef.current || !chartRef.current || !candleSeriesRef.current || !onPriceLineDrag) return;
    if (priceLines.length === 0) return;

    const container = containerRef.current;
    const chart = chartRef.current;
    const series = candleSeriesRef.current;
    let dragging = null; // { lineIndex, startY, originalPrice }

    const DRAG_THRESHOLD = 6; // pixels to detect near a price line

    const getLineAtY = (y) => {
      for (let i = 0; i < priceLines.length; i++) {
        const lineY = series.priceToCoordinate(priceLines[i].price);
        if (lineY != null && Math.abs(y - lineY) < DRAG_THRESHOLD) {
          return i;
        }
      }
      return -1;
    };

    const onMouseDown = (e) => {
      const rect = container.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const idx = getLineAtY(y);
      if (idx >= 0) {
        dragging = { lineIndex: idx, startY: y, originalPrice: priceLines[idx].price };
        container.style.cursor = 'ns-resize';
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const onMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      const y = e.clientY - rect.top;

      if (dragging) {
        const newPrice = series.coordinateToPrice(y);
        if (newPrice != null) {
          // Update the price line visually in real-time
          const pl = priceLinesRef.current[dragging.lineIndex];
          if (pl) pl.applyOptions({ price: newPrice });
        }
        e.preventDefault();
        e.stopPropagation();
      } else {
        // Show resize cursor when hovering near a price line
        const idx = getLineAtY(y);
        container.style.cursor = idx >= 0 ? 'ns-resize' : '';
      }
    };

    const onMouseUp = (e) => {
      if (!dragging) return;
      const rect = container.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const newPrice = series.coordinateToPrice(y);
      container.style.cursor = '';

      if (newPrice != null && newPrice !== dragging.originalPrice) {
        const line = priceLines[dragging.lineIndex];
        onPriceLineDrag(line.title, Math.round(newPrice * 100000) / 100000);
      }
      dragging = null;
    };

    container.addEventListener('mousedown', onMouseDown, true);
    container.addEventListener('mousemove', onMouseMove, true);
    container.addEventListener('mouseup', onMouseUp, true);
    container.addEventListener('mouseleave', () => {
      if (dragging) { dragging = null; container.style.cursor = ''; }
    });

    return () => {
      container.removeEventListener('mousedown', onMouseDown, true);
      container.removeEventListener('mousemove', onMouseMove, true);
      container.removeEventListener('mouseup', onMouseUp, true);
    };
  }, [priceLines, onPriceLineDrag]);

  // ── News event markers ──
  useEffect(() => {
    if (!candleSeriesRef.current) return;

    if (newsEvents && newsEvents.length > 0) {
      if (!newsPrimitiveRef.current) {
        newsPrimitiveRef.current = new NewsMarkerPrimitive();
        newsAttachedRef.current = false;
      }
      if (!newsAttachedRef.current) {
        try {
          candleSeriesRef.current.attachPrimitive(newsPrimitiveRef.current);
          newsAttachedRef.current = true;
        } catch {}
      }
      newsPrimitiveRef.current.setData(newsEvents);
    } else if (newsPrimitiveRef.current && newsAttachedRef.current) {
      try { candleSeriesRef.current.detachPrimitive(newsPrimitiveRef.current); } catch {}
      newsPrimitiveRef.current = null;
      newsAttachedRef.current = false;
    }
  }, [newsEvents]);

  // Build panel header labels
  const formatValue = (val) => {
    if (val == null || isNaN(val)) return '—';
    if (Math.abs(val) >= 1000) return val.toFixed(2);
    if (Math.abs(val) >= 1) return val.toFixed(4);
    return val.toFixed(6);
  };

  // Compute pane top offsets for header overlays
  // We use stretch factors: main=3, each panel=1, total = 3 + nPanels
  const nPanels = panelIndicators.length;
  const totalStretch = 3 + nPanels;

  return (
    <div ref={wrapperRef} className="chart-with-panels" style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={containerRef} className="lw-chart-container" />
      {watermark && <div className="chart-watermark" style={{ fontSize: `${watermarkSize}rem`, opacity: watermarkOpacity / 100, color: watermarkColor }}>{watermark}</div>}

      {/* ── News tooltip ── */}
      {newsTooltip && newsTooltip.events.length > 0 && (
        <div className="news-tooltip" style={{ left: Math.min(newsTooltip.x, (wrapperRef.current?.clientWidth || 600) - 250) }}>
          {newsTooltip.events.map((evt, i) => (
            <div key={i} className={`news-tooltip-item news-tooltip-${evt.impact}`}>
              <span className="news-tooltip-currency">{evt.currency}</span>
              <span className="news-tooltip-event">{evt.event}</span>
              <div className="news-tooltip-values">
                {evt.actual && <span>A: <b>{evt.actual}</b></span>}
                {evt.forecast && <span>F: {evt.forecast}</span>}
                {evt.previous && <span>P: {evt.previous}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Panel indicator headers (overlaid on each pane) ── */}
      {panelIndicators.map((panel, i) => {
        // Pane i+1 starts after main (3/totalStretch) + previous panels (i/totalStretch)
        const topPercent = ((3 + i) / totalStretch) * 100;

        const seriesLabels = panel.seriesData.map((sd) => {
          const parts = sd.key.split('_');
          const seriesKey = parts[parts.length - 1];
          const label = seriesKey === 'line' ? ''
            : seriesKey === 'histogram' ? 'Hist'
            : seriesKey === 'macdLine' ? 'MACD'
            : seriesKey === 'signalLine' ? 'Signal'
            : seriesKey === 'plusDI' ? '+DI'
            : seriesKey === 'minusDI' ? '-DI'
            : seriesKey.charAt(0).toUpperCase() + seriesKey.slice(1);
          return { key: sd.key, label, color: sd.color || '#8b8fa3' };
        });

        return (
          <div
            key={panel.instanceId}
            className="tv-panel-header-overlay"
            style={{ top: `${topPercent}%` }}
          >
            <div className="tv-indicator-header">
              <div className="tv-indicator-header-left">
                <span className="tv-indicator-title">{panel.title}</span>
                <div className="tv-indicator-values">
                  {seriesLabels.map((sl) => (
                    <span key={sl.key} className="tv-indicator-value" style={{ color: sl.color }}>
                      {sl.label && <span className="tv-indicator-value-label">{sl.label} </span>}
                      <span className="tv-indicator-value-num">
                        {formatValue(panelCrosshairValues[sl.key])}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
              <div className="tv-indicator-header-actions">
                {onOpenIndicatorSettings && (
                  <button className="tv-indicator-btn" onClick={() => onOpenIndicatorSettings(panel.instanceId)} title="Settings">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                    </svg>
                  </button>
                )}
                {onRemoveIndicator && (
                  <button className="tv-indicator-btn tv-indicator-btn--close" onClick={() => onRemoveIndicator(panel.instanceId)} title="Remove">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
});

export default CandlestickChart;
